/**
 * The home cluster (plan §5 R4; M2 spec §7.1): in the home-gate system, M1's frame origin sits at the centre of one
 * asteroid field. The three home stations keep M2's `stations.json` offsets from that origin (their bays face the
 * spawn, like M2's `faceToward: [0, 0, 0]`), the spawn is M1's, and the Harrow derelict keeps its `field.json` pose.
 */
import fieldJson from '../../data/field.json'
import { fieldConfigSchema, toFieldConfig, worldSchema } from '../../data/schemas'
import worldJson from '../../data/world.json'
import { derelictPose } from '../../world/field'
import { deriveSeed } from '../seed'
import type { StarSystem } from '../types'
import type { FactionsConfig, ReachSystemsConfig } from './config'
import { add, normalize, quatLookNegZ, sub } from './frame'
import type { FieldSite, HomeCluster, PoiSite, QuatT, StationSite, Vec3T } from './types'

/** What the home cluster takes from M1's data: the Harrow's pose relative to the frame origin, and its seed. */
export interface HomeInputs {
  derelict: { position: Vec3T; rotation: QuatT }
  /** `world.json` seed: the Harrow's `buildDerelict` seed (the sandbox field's too). */
  seed: number
}

let cachedHome: HomeInputs | null = null

/** The bundled `field.json` derelict pose and `world.json` seed, parsed once. */
export function defaultHomeInputs(): HomeInputs {
  if (cachedHome === null) {
    const pose = derelictPose(toFieldConfig(fieldConfigSchema.parse(fieldJson)))
    const { x, y, z, w } = pose.rotation
    cachedHome = {
      derelict: { position: [pose.position.x, pose.position.y, pose.position.z], rotation: [x, y, z, w] },
      seed: worldSchema.parse(worldJson).seed,
    }
  }
  return cachedHome
}

/** The field the home cluster sits in: `homeCluster.fieldIndex`, clamped to the system's fields. */
export function homeFieldIndex(fields: readonly unknown[], cfg: ReachSystemsConfig): number {
  return Math.min(cfg.homeCluster.fieldIndex, fields.length - 1)
}

export interface HomeClusterBuild {
  home: HomeCluster
  stations: StationSite[]
  harrow: PoiSite
}

/**
 * Builds the home cluster around `fields[homeFieldIndex]`. `poiIndex` is the Harrow's index in the system's POI
 * list (it goes after the generated POIs).
 */
export function buildHomeCluster(
  galaxySeed: number,
  system: StarSystem,
  fields: readonly FieldSite[],
  poiIndex: number,
  cfg: ReachSystemsConfig,
  factions: FactionsConfig,
  inputs: HomeInputs,
): HomeClusterBuild {
  const hc = cfg.homeCluster
  const field = fields[homeFieldIndex(fields, cfg)]
  const center = field.center
  const spawn = add(center, hc.spawnOffset)
  const stations = hc.stations.map((s, index): StationSite => {
    const t = cfg.stationTypes[s.type]
    const position = add(center, s.offset)
    return {
      id: `${system.id}-st${index}`,
      index,
      name: s.name,
      type: s.type,
      faction: s.faction,
      look: factions.factions[s.faction].look,
      assetId: t.assetId,
      position,
      rotation: quatLookNegZ(sub(spawn, position)),
      services: [...t.services, ...(s.extraServices ?? [])],
      m2Role: t.m2Role,
      specialty: s.specialty ?? null,
      priceSeed: deriveSeed(galaxySeed, 'layout', system.index, 'price', index),
      anchor: { kind: 'cluster', id: field.id },
    }
  })
  const hub = stations.find((s) => s.type === 'hub') ?? stations[0]
  const harrow: PoiSite = {
    id: `${system.id}-harrow`,
    index: poiIndex,
    kind: 'derelict',
    position: add(center, inputs.derelict.position),
    rotation: inputs.derelict.rotation,
    hidden: false,
    assetId: null,
    seed: inputs.seed,
    baseSide: null,
  }
  return {
    home: { center, fieldId: field.id, spawn, spawnFacing: normalize(sub(hub.position, spawn)), harrowPoiId: harrow.id },
    stations,
    harrow,
  }
}
